import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ACTOR_PHRASES } from '../metadata/actor_sentence';
import AgentMetadata from '../metadata/agent_metadata';
import ApiMetadata from '../metadata/api_metadata';
import Communicator, {
  isApiInitiator as checkIsApiInitiator,
  isGladlyAiInitiator,
  isRuleInitiator,
} from 'models/communicator';
import connect from 'components/lib/connect';
import ConversationItem from 'models/conversation_item';
import ItemContent from '../item_content';
import Metadata from '../metadata/metadata';
import PrepositionalPhrase from '../metadata/prepositional_phrase';
import RuleLink from '../metadata/rule_link';
import { TaskLink } from '../task/task_routing_item';

// TaskTopicChangeItem renders the customer-timeline audit line for a topic
// change on a task (backend content type TASK_TOPIC_CHANGE). Mirrors
// TopicChangeItem but appends the task the topic change applied to, linked —
// "to <Task>" when added, "from <Task>" when removed (e.g. "You added topic
// Refund to Task", "You removed topic Refund from Task").
export function TaskTopicChangeItem({ addedTopics, item, removedTopics, taskId }) {
  const { t } = useTranslation();
  const isApiInitiator = checkIsApiInitiator(item.initiator);

  return (
    <ItemContent isAuditItem item={item}>
      {getTopicChangeContent()}
    </ItemContent>
  );

  function taskPhrase(preposition) {
    return (
      <PrepositionalPhrase
        complement={<TaskLink className="itemMetadata-subject" taskItemId={taskId} />}
        key="task"
        preposition={preposition}
        suppressHighlight
      />
    );
  }

  function getTopicChangeContent() {
    if (item.initiator.type === Communicator.AGENT) {
      return getAgentTopicChangeContent();
    }

    if (isApiInitiator) {
      return getNonAgentTopicChangeContent(ApiMetadata);
    }

    return getAutomatedTopicChangeContent();
  }

  function getAgentTopicChangeContent() {
    return (
      <>
        {addedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="taskTopicAdded">
            <AgentMetadata
              item={item}
              phrase={addedTopics.length > 1 ? ACTOR_PHRASES.ADDED_TOPICS : ACTOR_PHRASES.ADDED_TOPIC}
              phraseValues={{ topics: addedTopics.join(', ') }}
              prepositionalPhrases={taskPhrase(t('to'))}
            />
          </TopicChangeDisplayItem>
        ) : null}
        {removedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="taskTopicRemoved">
            <AgentMetadata
              item={item}
              phrase={removedTopics.length > 1 ? ACTOR_PHRASES.REMOVED_TOPICS : ACTOR_PHRASES.REMOVED_TOPIC}
              phraseValues={{ topics: removedTopics.join(', ') }}
              prepositionalPhrases={taskPhrase(t('from'))}
            />
          </TopicChangeDisplayItem>
        ) : null}
      </>
    );
  }

  function getAutomatedTopicChangeContent() {
    return (
      <>
        {addedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="taskTopicAdded">
            <Metadata
              isInbound={false}
              prepositionalPhrases={getNonAgentTopicPrepositionalPhrases(addedTopics, t('added'), t('to'))}
              timestamp={item.timestamp}
            />
          </TopicChangeDisplayItem>
        ) : null}
        {removedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="taskTopicRemoved">
            <Metadata
              isInbound={false}
              prepositionalPhrases={getNonAgentTopicPrepositionalPhrases(removedTopics, t('removed'), t('from'))}
              timestamp={item.timestamp}
            />
          </TopicChangeDisplayItem>
        ) : null}
      </>
    );
  }

  function getNonAgentTopicChangeContent(MetadataComponent) {
    return (
      <>
        {addedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="taskTopicAdded">
            <MetadataComponent
              item={item}
              prepositionalPhrases={getNonAgentTopicPrepositionalPhrases(addedTopics, t('added'), t('to'))}
            />
          </TopicChangeDisplayItem>
        ) : null}
        {removedTopics.length > 0 ? (
          <TopicChangeDisplayItem data-aid="taskTopicRemoved">
            <MetadataComponent
              item={item}
              prepositionalPhrases={getNonAgentTopicPrepositionalPhrases(removedTopics, t('removed'), t('from'))}
            />
          </TopicChangeDisplayItem>
        ) : null}
      </>
    );
  }

  // `action` is an already-translated verb (t('added') / t('removed')) — it has
  // to be translated at the call sites so the keys stay static and extractable.
  function getNonAgentTopicPrepositionalPhrases(topics, action, taskPreposition) {
    const prepositionalPhrases = [];

    prepositionalPhrases.push(
      <PrepositionalPhrase
        complement={topics.join(', ')}
        key="topic"
        preposition={topics.length > 1 ? t('Topics') : t('Topic')}
      />
    );

    let complement = '';
    let preposition = action;
    if (isRuleInitiator(item.initiator)) {
      complement = <RuleLink ruleId={item.initiator.id}>{t('rule')}</RuleLink>;
      preposition = `${preposition} ${t('by')}`;
    } else if (isGladlyAiInitiator(item.initiator)) {
      complement = t('Gladly AI');
      preposition = `${preposition} ${t('by')}`;
    }

    prepositionalPhrases.push(<PrepositionalPhrase complement={complement} key="by" preposition={preposition} />);
    prepositionalPhrases.push(taskPhrase(taskPreposition));
    return prepositionalPhrases;
  }
}

TaskTopicChangeItem.propTypes = {
  addedTopics: PropTypes.arrayOf(PropTypes.string).isRequired,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  removedTopics: PropTypes.arrayOf(PropTypes.string).isRequired,
  taskId: PropTypes.string,
};

const TopicChangeDisplayItem = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  max-width: 100%;
`;

function mapStateToProps({ getProvider }, { item }) {
  const addedTopics = _.sortBy(getTopicNames(item.content.addedTopics), topic => topic.toLowerCase());
  const removedTopics = _.sortBy(getTopicNames(item.content.removedTopics), topic => topic.toLowerCase());

  return {
    addedTopics,
    item,
    removedTopics,
    taskId: item.content.taskId,
  };

  function getTopicNames(topicIds) {
    const topicNames = [];
    topicIds.forEach(topicId => {
      const topic = getProvider('topics').findBy({ id: topicId });
      if (topic) {
        topicNames.push(topic.getNameWithAncestry());
      }
    });
    return topicNames;
  }
}

const TaskTopicChangeItemContainer = connect(mapStateToProps, null)(TaskTopicChangeItem);
TaskTopicChangeItemContainer.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

export default TaskTopicChangeItemContainer;
